import { traverse } from './traverse.js';

// Helper function to create appropriate container type
function createNodeType (a) {
  return typeof a === 'number' ? [] : {};
}


const graftFunctions = [
  null, // index 0
  null, // index 1
  (state, path, value) => {
    const [a, b] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = value;
    return value;
  },
  (state, path, value) => {
    const [a, b, c] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = b in state[a] ? state[a][b] : createNodeType(c);
    state[a][b][c] = value;
    return value;
  },
  (state, path, value) => {
    const [a, b, c, d] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = b in state[a] ? state[a][b] : createNodeType(c);
    state[a][b][c] = c in state[a][b] ? state[a][b][c] : createNodeType(d);
    state[a][b][c][d] = value;
    return value;
  },
  (state, path, value) => {
    const [a, b, c, d, e] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = b in state[a] ? state[a][b] : createNodeType(c);
    state[a][b][c] = c in state[a][b] ? state[a][b][c] : createNodeType(d);
    state[a][b][c][d] = d in state[a][b][c] ? state[a][b][c][d] : createNodeType(e);
    state[a][b][c][d][e] = value;
    return value;
  },
  (state, path, value) => {
    const [a, b, c, d, e, f] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = b in state[a] ? state[a][b] : createNodeType(c);
    state[a][b][c] = c in state[a][b] ? state[a][b][c] : createNodeType(d);
    state[a][b][c][d] = d in state[a][b][c] ? state[a][b][c][d] : createNodeType(e);
    state[a][b][c][d][e] = e in state[a][b][c][d] ? state[a][b][c][d][e] : createNodeType(f);
    state[a][b][c][d][e][f] = value;
        return value;
  },
  (state, path, value) => {
    const [a, b, c, d, e, f, g] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = b in state[a] ? state[a][b] : createNodeType(c);
    state[a][b][c] = c in state[a][b] ? state[a][b][c] : createNodeType(d);
    state[a][b][c][d] = d in state[a][b][c] ? state[a][b][c][d] : createNodeType(e);
    state[a][b][c][d][e] = e in state[a][b][c][d] ? state[a][b][c][d][e] : createNodeType(f);
    state[a][b][c][d][e][f] = f in state[a][b][c][d][e] ? state[a][b][c][d][e][f] : createNodeType(g);
    state[a][b][c][d][e][f][g] = value;
    return value;
  },
  (state, path, value) => {
    const [a, b, c, d, e, f, g, h] = path;
    state[a] = a in state ? state[a] : createNodeType(b);
    state[a][b] = b in state[a] ? state[a][b] : createNodeType(c);
    state[a][b][c] = c in state[a][b] ? state[a][b][c] : createNodeType(d);
    state[a][b][c][d] = d in state[a][b][c] ? state[a][b][c][d] : createNodeType(e);
    state[a][b][c][d][e] = e in state[a][b][c][d] ? state[a][b][c][d][e] : createNodeType(f);
    state[a][b][c][d][e][f] = f in state[a][b][c][d][e] ? state[a][b][c][d][e][f] : createNodeType(g);
    state[a][b][c][d][e][f][g] = g in state[a][b][c][d][e][f] ? state[a][b][c][d][e][f][g] : createNodeType(h);
    state[a][b][c][d][e][f][g][h] = value;
    return value;
  }
];

function createGraftFunction(depth) {
  // Instead of alphabet, use numbered variables
  const names = Array.from({ length: depth }, (_, i) => `v${i}`);
  const args = names.join(', ');
  let body = `const [${args}] = path;\n`;

  let pathSoFar = 'state';
  for (let i = 0; i < depth - 1; i++) {
    const current = names[i];
    const next = names[i + 1];
    body += `${pathSoFar}[${current}] = (${pathSoFar}[${current}] !== undefined && typeof ${pathSoFar}[${current}] === 'object') ? ${pathSoFar}[${current}] : createNodeType(${next});\n`;
    pathSoFar += `[${current}]`;
  }

  body += `${pathSoFar}[${names[depth - 1]}] = value;\nreturn state;`;

  const fn = new Function('state', 'path', 'value', 'createNodeType', body);
  graftFunctions[depth] = (state, path, value) => fn(state, path, value, createNodeType);
  return graftFunctions[depth];
}

export function graft(state, path, value) {
  const len = path.length;

  if (len === 0) {
    return Object.assign(state, value);
  }

  if (len === 1) {
    return state[path[0]] = value;
  }
  
  // Use or create cached function
  if (!graftFunctions[len]) {
    return createGraftFunction(len)(state, path, value);
  }
  
  return graftFunctions[len](state, path, value);
}

export function detach(state, path) {
  const len = path.length;
  
  if (len === 0) {
    return undefined;
  }

  if (len === 1) {
    const value = state[path[0]];
    if (Array.isArray(state)) {
      state.splice(path[0], 1);
    } else {
      delete state[path[0]];
    }
    return value;
  }

  const parentPath = path.slice(0, -1);
  const last = path[len - 1];
  const parent = traverse(state, parentPath);
  
  if (!parent) return undefined;
  
  const value = parent[last];
  if (Array.isArray(parent)) {
    parent.splice(last, 1);
  } else {
    delete parent[last];
  }
  return value;
}

// tree/mutate.js
export function replace(oldState, newState) {
  // Get all existing keys
  const oldKeys = Object.keys(oldState);
  let i = 0;
  
  // Clear existing properties
  while (i < oldKeys.length) {
    delete oldState[oldKeys[i]];
    i++;
  }

  // Get all new keys
  const newKeys = Object.keys(newState);
  i = 0;

  // Deep merge new properties
  while (i < newKeys.length) {
    const key = newKeys[i];
    const value = newState[key];
    
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      oldState[key] = oldState[key] || {};
      replace(oldState[key], value);
    } else {
      oldState[key] = value;
    }
    
    i++;
  }

  return oldState;
}

export function clear(state) {
  const keys = Object.keys(state);
  for (const key of keys) {
    delete state[key];
  }
  return state;
}

export function fuse(oldState, newState) {
  return Object.assign(oldState, newState);
}